<template>
  <SoWidget data-testid="draw-detail-prizes">
    <template #title>
      <div class="d-flex align-center justify-space-between">
        <span>Prizes</span>
        <SoButton
          v-if="editable"
          title="Add prize"
          type="primary"
          data-testid="prize-add-btn"
          @click="openAddModal"
        />
      </div>
    </template>
    <SoDataTable
      :key="editable ? 'editable' : 'readonly'"
      :headers="headers"
      :items="items"
      :loading="loading"
      :searchable="false"
      :has-pagination="false"
      data-testid="draw-detail-prizes-table"
    >
      <template v-if="editable" #itemActions="{ internalItem }">
        <div class="d-flex gap-1 justify-end">
          <SoButton
            title="Edit"
            type="table-action"
            data-testid="prize-edit-btn"
            @click="openEditModal(internalItem.raw.id)"
          />
          <SoButton
            title="Delete"
            type="table-action"
            data-testid="prize-delete-btn"
            @click="openDeleteModal(internalItem.raw.id)"
          />
        </div>
      </template>
    </SoDataTable>

    <template v-if="editable">
      <OpsDrawPrizeFormModal
        v-model="showFormModal"
        :draw-id="drawId"
        :reward="selectedReward"
        @saved="onSaved"
      />
      <OpsDrawPrizeDeleteModal
        v-model="showDeleteModal"
        :draw-id="drawId"
        :reward="selectedReward"
        @saved="onSaved"
      />
    </template>
  </SoWidget>
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDataTable from "@/components/dataTable/SoDataTable.vue"
  import SoWidget from "@/components/SoWidget.vue"
  import OpsDrawPrizeFormModal from "./OpsDrawPrizeFormModal.vue"
  import OpsDrawPrizeDeleteModal from "./OpsDrawPrizeDeleteModal.vue"
  import { SoDataTableHeader } from "@/types/components/SoDataTable"
  import { IDrawReward } from "@/models/operations/draw/drawReward.types"
  import { formatMoney } from "@/helpers/moneyHelpers"
  import { REWARD_TYPE_LABELS } from "./drawRewardTypeConfig"

  type PrizeRow = {
    id: string
    name: string
    type: string
    quantity: number
    projectedCost: string
  }

  // SoDataTable auto-appends the actions column when the #itemActions slot is
  // present — declaring it here would shadow that slot with an empty cell.
  const headers: SoDataTableHeader[] = [
    { title: "Name", key: "name" },
    { title: "Type", key: "type" },
    { title: "Quantity", key: "quantity" },
    { title: "Projected cost", key: "projectedCost" },
  ]

  export default defineComponent({
    components: {
      SoButton,
      SoDataTable,
      SoWidget,
      OpsDrawPrizeFormModal,
      OpsDrawPrizeDeleteModal,
    },
    props: {
      rewards: {
        type: Array as PropType<IDrawReward[]>,
        required: true,
      },
      drawId: {
        type: String,
        required: true,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      editable: {
        type: Boolean,
        default: false,
      },
    },
    emits: ["saved"],
    data() {
      return {
        headers,
        showFormModal: false,
        showDeleteModal: false,
        selectedReward: null as IDrawReward | null,
      }
    },
    computed: {
      items(): PrizeRow[] {
        return this.rewards.map((r) => ({
          id: r.id,
          name: r.name,
          type: REWARD_TYPE_LABELS[r.type] ?? r.type,
          quantity: r.quantity,
          projectedCost:
            r.projectedCostPence === null
              ? "N/A"
              : formatMoney({ amount: r.projectedCostPence / 100 }),
        }))
      },
    },
    methods: {
      findReward(id: string): IDrawReward | null {
        return this.rewards.find((r) => r.id === id) ?? null
      },
      openAddModal() {
        this.selectedReward = null
        this.showFormModal = true
      },
      openEditModal(id: string) {
        this.selectedReward = this.findReward(id)
        this.showFormModal = true
      },
      openDeleteModal(id: string) {
        this.selectedReward = this.findReward(id)
        this.showDeleteModal = true
      },
      onSaved() {
        this.$emit("saved")
      },
    },
  })
</script>
